import { useMemo } from 'react';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';

export function useSafeSessionStorage<TData>(
    key: string,
    initializer: (serializedData: string | null) => TData
): ReturnType<typeof useSessionStorage<TData>> {
    return useSessionStorage<TData>(key, () => {
        const currentExpanded = sessionStorage.getItem(key);

        return currentExpanded ? JSON.parse(currentExpanded) : initializer(currentExpanded);
    });
}

export function useSafeLocalStorage<TData>(
    key: string,
    initializer: (serializedData: string | null) => TData
): ReturnType<typeof useLocalStorage<TData>> {
    return useLocalStorage<TData>(key, () => {
        const currentExpanded = localStorage.getItem(key);

        return currentExpanded ? JSON.parse(currentExpanded) : initializer(currentExpanded);
    });
}

export function buildKey(parts: (string | number)[]) {
    return parts.join('::');
}

type UseResizableTableStorageKeyProps = {
    base: string;
    utility: string;
    id: string;
    version: number;
};

export function useStorageKey({ base, utility, id, version }: UseResizableTableStorageKeyProps) {
    return useMemo(() => {
        return buildKey([base, utility, id, version]);
    }, [base, id, utility, version]);
}

type UsePersistFilterStorageKeyProps = {
    id: string;
};

export function usePersistFilterStorageKey({ id }: UsePersistFilterStorageKeyProps) {
    return useMemo(() => {
        return buildKey([id, '__persisted']);
    }, [id]);
}

export function getStoredFilters(key: string) {
    const storedFilters = localStorage.getItem(key);

    const parsed: unknown[] = JSON.parse(storedFilters ?? '[]');

    return parsed;
}
